import { useNavigate } from "react-router-dom-v5-compat";
import { CarsRawEventName, useEmitApacCarsRawEvent } from "@apac/cars";
import {
  CoreCarsComponent,
  CarDescription,
  CarCardPrice,
  useShopLink,
  type CarCardProps as ApacCarCardProps,
  CarSpecificationsSummary,
} from "@apac/components/cars";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Funnel } from "@b2bportal/core-types";
import { RestrictionCancelEnum } from "@b2bportal/ground-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { usePayWithPointsPrice } from "@hsbc/rewards";
import clsx from "clsx";
import styles from "./styles.module.scss";

export type CarCardProps = ApacCarCardProps & {
  view?: "availability" | "shop" | "review";
};

export function CarCard({
  className,
  availability,
  view = "availability",
}: CarCardProps) {
  const [block, cn] = useModuleBEM(styles, CoreCarsComponent.CarCard);
  const shopView = view === "shop";
  const reviewView = view === "review";

  const { t, formatFiatCurrency } = useI18nContext();
  const {
    vehicle,
    supplier,
    totalCharge,
    dailyCharge,
    cancellationPolicy,
    hasUnlimitedMileage,
  } = availability;
  const shopLink = useShopLink({ vehicleId: vehicle.id, skipStore: true });
  const navigate = useNavigate();
  const { getFormattedPayWithPointsMessage } = usePayWithPointsPrice({
    funnel: Funnel.Ground,
  });
  const formattedPayWithPointsMessage = getFormattedPayWithPointsMessage(
    totalCharge.fiat
  );

  const emitClickedCarEvent = useEmitApacCarsRawEvent(
    CarsRawEventName.ClickedCarOnAvailability
  );

  if (supplier == null) {
    return null;
  }

  if (dailyCharge == null) {
    return null;
  }

  const isFreeCancellation =
    cancellationPolicy?.RestrictionCancel !== RestrictionCancelEnum.Paid &&
    cancellationPolicy?.RestrictionCancel !==
      RestrictionCancelEnum.NonRefundable;

  return (
    <button
      onClick={() => {
        emitClickedCarEvent();
        navigate(shopLink);
      }}
      className={clsx(block, className, {
        [cn("shop-view")]: shopView,
        [cn("review-view")]: reviewView,
      })}
    >
      <CarDescription
        className={cn("description")}
        supplier={supplier}
        vehicle={vehicle}
        isFreeCancellation={isFreeCancellation}
        hasUnlimitedMileage={hasUnlimitedMileage}
        collisionDamageWaiver={availability.collisionDamageWaiver}
      />
      <CarSpecificationsSummary
        className={cn("specifications")}
        vehicle={vehicle}
      />
      <div
        className={cn("separator", {
          show: !shopView && !reviewView,
        })}
      />
      <CarCardPrice
        position={shopView ? "right" : "left"}
        className={cn("pricing", { show: !reviewView })}
        totalPrice={formatFiatCurrency(totalCharge.fiat)}
        pricePerDay={formatFiatCurrency(dailyCharge.fiat)}
        additionalPriceInfo={
          shopView && (
            <div className={cn("pay-with-points")}>
              {formattedPayWithPointsMessage}
            </div>
          )
        }
      />
      <div
        className={cn("button", {
          show: !shopView && !reviewView,
        })}
      >
        {t("apac.cars.reserve")}
      </div>
    </button>
  );
}
